/**
 * The numbers on the main Overview, as plain functions (they used to be worked out inside the
 * screen). Screens only draw what these return. Amounts are micro (millionths of a dollar).
 */
import type { Micro } from "./money.ts";
import { shownCents } from "./shown.ts";
import type { GroupState, IsoDate } from "./types.ts";
import type { Ledger } from "./engine/ledger.ts";
import { monthsBetween } from "./engine/trends.ts";
import {
  isSeeking,
  isUnclaimed,
  studentSummary,
  tutorSummary,
  type Tutor,
  type TutoringClaim,
  type TutoringEntry,
  type TutoringSlot,
  type TutoringStudent,
} from "./tutoring.ts";

const CENT: Micro = 10_000;

/** What people owe you in one group: the sum of what each row shows, so no phantom cents. */
export function owedToYouMicro(ledger: Ledger): Micro {
  return ledger.people
    .filter((p) => p.status === "owes")
    .reduce((s, p) => s + shownCents(p.balanceMicro) * CENT, 0);
}

/** What you owe people in one group, as a positive amount, from what each row shows. */
export function youOweMicro(ledger: Ledger): Micro {
  return ledger.people
    .filter((p) => p.status === "owed")
    .reduce((s, p) => s - shownCents(p.balanceMicro) * CENT, 0);
}

/**
 * The last `count` calendar months ending with today's month, oldest first ("2025-11" ... "2026-10").
 * Counted in whole months, so the 29th, 30th and 31st never skip a month.
 */
export function trailingMonths(today: IsoDate, count = 12): string[] {
  const [y, m] = today.slice(0, 7).split("-").map(Number) as [number, number];
  const index = y * 12 + (m - 1) - (count - 1);
  const from = `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, "0")}`;
  return monthsBetween(from, today.slice(0, 7));
}

/** The date `days` days before `today`. */
export function daysAgo(today: IsoDate, days: number): IsoDate {
  const d = new Date(`${today}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

/**
 * Money that came in over the last `days` days: payments to you from others, across groups.
 * Tutoring pay that was put into a group as a System payment isn't money that came in.
 */
export function receivedSinceMicro(
  states: readonly GroupState[],
  today: IsoDate,
  days = 30,
): Micro {
  const from = daysAgo(today, days);
  let sum = 0;
  for (const s of states) {
    const me = s.settings.ownerPersonId;
    for (const id of s.paymentOrder) {
      const p = s.payments[id];
      if (p && p.cents > 0 && p.personId !== me && p.at.slice(0, 10) >= from) sum += p.cents * CENT;
    }
  }
  return sum;
}

/** The first and last purchase dates across groups (the range slider's ends). */
export function purchaseSpan(states: readonly GroupState[]): {
  lo: IsoDate | null;
  hi: IsoDate | null;
} {
  let lo = "";
  let hi = "";
  for (const s of states)
    for (const it of Object.values(s.items)) {
      if (!lo || it.date < lo) lo = it.date;
      if (!hi || it.date > hi) hi = it.date;
    }
  return { lo: lo || null, hi: hi || null };
}

export interface LabelledAmount {
  label: string;
  totalMicro: Micro;
}

/** Families who owe you for tutoring, biggest balance kept as is (sorting is the screen's job). */
export function familiesWhoOwe(
  students: readonly TutoringStudent[],
  entries: readonly TutoringEntry[],
): LabelledAmount[] {
  return students
    .filter((s) => !s.archived)
    .map((s) => ({
      label: `${s.family || s.name} · Tutoring`,
      totalMicro: studentSummary(s.id, entries).balanceCents * CENT,
    }))
    .filter((x) => x.totalMicro > 0);
}

/** Tutors whose tutoring pay is credited to them (what you owe them for teaching). */
export function tutorsYouOwe(
  tutors: readonly Tutor[],
  entries: readonly TutoringEntry[],
): LabelledAmount[] {
  return tutors
    .filter((t) => !t.archived)
    .map((t) => ({
      label: `${t.name} · Tutoring`,
      totalMicro: tutorSummary(t, entries).owedCents * CENT,
    }))
    .filter((x) => x.totalMicro > 0);
}

/**
 * Tutoring as a card on the Overview, next to the groups (Miles, 2026-11-02: "make it show up in
 * overview just like a group"). Owed to you is what families owe (each one's balance above zero),
 * you owe is tutors' pay not paid yet: the same figures as main People's tutoring lines.
 * `now` is a Los Angeles local time ("2026-11-02T16:30"), like every lesson time.
 */
export interface TutoringCard {
  owedToYouCents: number;
  youOweCents: number;
  students: number;
  tutors: number;
  /** Lessons this month that are done or still booked (not cancelled, not "Didn't happen"). */
  lessonsThisMonth: number;
  /** The latest done lesson that has started, as its start ("2026-10-22T16:00"). */
  lastLesson: string | null;
  /** The next booked lesson that hasn't started. */
  nextLesson: string | null;
  /** Upcoming lessons and students waiting for a tutor. */
  needsTutor: number;
  /** Tutors' requests waiting for your OK. */
  requests: number;
}

export function tutoringCard(
  students: readonly TutoringStudent[],
  tutors: readonly Tutor[],
  entries: readonly TutoringEntry[],
  slots: readonly TutoringSlot[],
  claims: readonly TutoringClaim[],
  now: string,
): TutoringCard {
  const month = now.slice(0, 7);
  const live = slots.filter((s) => s.status === "done" || s.status === "booked");
  // A lesson marked done early can still be ahead: the last lesson is the latest one already started.
  const done = live.filter((s) => s.status === "done" && s.startsAt <= now).map((s) => s.startsAt);
  const next = live
    .filter((s) => s.status === "booked" && s.studentId && s.startsAt > now)
    .map((s) => s.startsAt)
    .sort();
  return {
    owedToYouCents: familiesWhoOwe(students, entries).reduce((t, x) => t + x.totalMicro / CENT, 0),
    youOweCents: tutorsYouOwe(tutors, entries).reduce((t, x) => t + x.totalMicro / CENT, 0),
    students: students.filter((s) => !s.archived).length,
    tutors: tutors.filter((t) => !t.archived).length,
    lessonsThisMonth: live.filter((s) => s.studentId && s.startsAt.slice(0, 7) === month).length,
    lastLesson: done.sort().at(-1) ?? null,
    nextLesson: next[0] ?? null,
    needsTutor:
      slots.filter((s) => isUnclaimed(s) && s.startsAt > now).length +
      students.filter(isSeeking).length,
    requests: claims.filter((c) => c.status === "pending").length,
  };
}

/**
 * The same card for a family or a tutor looking at their own Tutoring. The balance uses the core's
 * sign, like BalanceAmount: positive is what they owe, negative is pay still owed to them (shown
 * on screen from their side: minus in red is owed, plus in green a credit).
 */
export interface MyTutoringCard {
  balanceCents: number;
  lessonsThisMonth: number;
  nextLesson: string | null;
  /** Tutors: lessons and students they could take. */
  upForGrabs: number;
}

export function myTutoringCard(
  students: readonly TutoringStudent[],
  tutors: readonly Tutor[],
  entries: readonly TutoringEntry[],
  slots: readonly TutoringSlot[],
  open: { lessons: number; students: number },
  now: string,
): MyTutoringCard {
  const owes = students.reduce((t, s) => t + studentSummary(s.id, entries).balanceCents, 0);
  const owed = tutors.reduce((t, x) => t + tutorSummary(x, entries).owedCents, 0);
  const mine = (s: TutoringSlot) =>
    students.some((x) => x.id === s.studentId) || tutors.some((x) => x.id === s.tutorId);
  const live = slots.filter((s) => (s.status === "done" || s.status === "booked") && mine(s));
  return {
    balanceCents: owes - owed,
    lessonsThisMonth: live.filter((s) => s.startsAt.slice(0, 7) === now.slice(0, 7)).length,
    nextLesson:
      live
        .filter((s) => s.status === "booked" && s.startsAt > now)
        .map((s) => s.startsAt)
        .sort()[0] ?? null,
    upForGrabs: tutors.length ? open.lessons + open.students : 0,
  };
}
